/**
 * NE4 (.334): Aufträge und Import, Auftragskarte, Anrufliste.
 *
 * ORIGINALTEXT MARVIN (08.10.2026)
 *   „Ich hätte gerne, dass das System logische Vorschläge macht, wenn ein
 *    Kunde da ist. … da ist ein Termin ausgefallen und frei in dem Gebiet.
 *    Wenn es nicht in dem Gebiet ist, … wann ist das angrenzende nächste
 *    Gebiet."
 *
 * DIE KARTE IST EIN DIALOG, nicht eine Seite: der Innendienst hat den Kunden
 * am Telefon und braucht alles auf einem Blick, ohne Scrollen: oben die
 * Vorschläge, dann Kunde und Nummern zum Wählen, die 6 Ergebnis-Klicks, der
 * Verlauf.
 */
import { MailVorschau } from '../teile/Ne4Mailvorschau.tsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, darf, datum, zeitpunkt, type Anmelder } from '../api.ts';
import { auftragsBetreff, offenAusAdresse, offenInAdresse, quicklink } from '../teile/adresse.ts';
import { MailMitAdressbuch } from '../teile/MailMitAdressbuch.tsx';
import { Kopierbar } from '../teile/Kopierbar.tsx';
import { connectAbgleich } from '../teile/ne4abgleich.ts';
import { Suchfeld, passtSuche, useVerzoegert } from '../teile/Schluesselsuche.tsx';
import { Kopf, Karte } from '../teile/Geruest.tsx';
import { Filterklappe } from '../teile/Filterklappe.tsx';
import { Dialog, Fehlerkasten, Text, Auswahl, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { BlockDialog, HausBlock, type HausReiter } from '../teile/Ne4Haus.tsx';
import { TelekomMeldung } from '../teile/Ne4Telekommeldung.tsx';
import Auftragsinfoliste, { type Infozeile } from '../teile/Auftragsinfoliste.tsx';

export const ZUSTAND: Record<string, string> = {
  import: 'Neu', angeschrieben: 'Angeschrieben', wartet: 'Wartet auf Kunde', terminiert: 'Terminiert',
  ausgefuehrt: 'Ausgeführt', abgerechnet: 'Abgerechnet', zurueckgegeben: 'Zurückgegeben',
};
const ZUSTAND_KLASSE: Record<string, string> = {
  import: 'z-neu', angeschrieben: 'z-wartet', wartet: 'z-wartet', terminiert: 'z-laufend', ausgefuehrt: 'z-erledigt', abgerechnet: 'z-abgerechnet', zurueckgegeben: 'z-unterbrochen',
};
export const KONTAKT: Record<string, string> = {
  mail_einladung1: 'Einladung per Mail', mail_einladung2: 'Zweite Einladung', mail_ankuendigung: 'Ankündigung per Mail', mail_bestaetigung: 'Terminbestätigung',
  mail_erinnerung3: 'Erinnerung 3 Tage vorher', mail_erinnerung1: 'Erinnerung Vortag', anruf: 'Anruf', buchung: 'Termin gebucht', absage: 'Termin abgesagt',
  verschiebung: 'Termin verschoben', fertig: 'Fertig gemeldet', nicht_erledigt: 'Nicht erledigt', rueckgabe: 'Rückgabe an die Telekom', rueckgabe_vorgemerkt: 'Zur Rückgabe vorgemerkt',
  notiz: 'Notiz', gbgs: 'GBGS', begonnen: 'In Arbeit genommen', verzoegerung: 'Verzögerung gemeldet', partner: 'Von der Firma', mail_haus_bestaetigung: 'Terminbestätigung an den Eigentümer', mail_haus_aushang: 'Aushang an den Eigentümer',
  mail_haus_nachfassen: 'Nachfass-Mail an den Eigentümer', mail_haus_erinnerung: 'Erinnerung an den Eigentümer', import: 'Aus GBGS eingelesen',
  /* .620 */ zustand: 'Status von Hand', mail_telekom_meldung: 'Meldung an die Telekom',
};
const ERGEBNIS: Record<string, string> = {
  gbgs: 'Bulk-Portal', roboter: 'Roboter', eingetragen: 'eingetragen', geaendert: 'geändert',
  termin_gebucht: 'Termin gebucht', nicht_erreicht: 'Nicht erreicht', rueckruf: 'Rückruf gewünscht', mail_erneut: 'Mail erneut gesendet',
  kunde_lehnt_ab: 'Kunde lehnt ab', falsche_nummer: 'Falsche Nummer', gewaehlt: 'gewählt', gesendet: 'gesendet', tag: 'Tag des Blocks', bewohner_informiert: 'Bewohner informiert', unterlage: 'Unterlage beigefügt', 'nicht gesendet': 'nicht gesendet', kunde: 'über den Link', telefon: 'am Telefon', dispo: 'durch die Dispo',
};

export const tagLang = (t: string) => new Date(t + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
/* UHRZEIT OHNE SEKUNDEN (.400). Marvin, 19.10.2026: „Datumsformat/Uhrzeit
   nicht europäisch." `beginn` kommt als „08:00:00" aus der Datenbank. */
export const uhr = (t: unknown) => String(t ?? '').slice(0, 5);
/* DER MONAT MIT NAMEN: „Oktober 2026" statt „2026-10". */
export const monatName = (m: string) => { const [j, mo] = m.split('-').map(Number); return j && mo ? new Date(j, mo - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }) : m; };
/* DIE WOCHE MIT NAMEN: „KW 42" statt „2026-42". */
export const wocheName = (w: string) => { const m = /^(\d{4})-(\d{2})$/.exec(String(w)); return m ? `KW ${Number(m[2])} / ${m[1]}` : w; };

/* WÄHLEN MIT PROTOKOLL (.345). ORIGINALTEXT MARVIN (11.10.2026): „Sobald er
   dort auf Anrufen klickt, wird er ja weitergeleitet, jetzt über Webex. Und
   dann möchte ich protokollieren im Auftrag, wer hat wann versucht, den
   Teilnehmer anzurufen." Erst der Vermerk, dann die Wahl über tel:, die
   das Telefonprogramm (Webex) übernimmt. */
export async function waehlen(auftragId: string, welche: 'mobil' | 'telefon', nummer: string): Promise<void> {
  try { await api.ne4Kontakt(auftragId, { ergebnis: 'gewaehlt', welche, nummer }); } catch { /* die Wahl geht trotzdem */ }
  window.location.href = `tel:${nummer.replace(/\s+/g, '')}`;
}
function Wahlknopf({ auftragId, welche, nummer, nach }: { auftragId: string; welche: 'mobil' | 'telefon'; nummer: string | null; nach?: () => void }) {
  if (!nummer) return null;
  return (
    <button type="button" className="knopf klein" onClick={(e) => { e.stopPropagation(); waehlen(auftragId, welche, nummer).then(() => nach?.()); }}
            title="Vermerkt im Auftrag, wer wann diese Nummer gewählt hat, und wählt dann">
      📞 {welche === 'mobil' ? 'Mobil' : 'Festnetz'} {nummer}
    </button>
  );
}

/* .593: DIE AUFTRAGSINFO IN DER LISTE WIE BEI NE3: der jüngste Eintrag, wann und von wem, dazu die Zahl.
   Marvin, 02.11.2026: „Wo wurde schon was geschrieben? Wo wurde schon was gemacht? Was ist der letzte Status?" */
/* .609: KEINE PUNKTE ALS TRENNER. Marvin, 03.11.2026: „wir trennen nichts mit Punkten ... Wir trennen alles ganz
   normal mit Komma, Leerzeichen." Gilt auch für ältere Einträge, die so gespeichert sind. */
export const ohnePunkte = (s: string) => s.replace(/\s+·\s+/g, ', ');

/* .609: DER TERMIN MIT ZEITRAUM, FIRMA UND TEAM. Marvin, 03.11.2026: „für wen terminiert ist, das musste richtig
   darstellen, für welche Firma und welches Team. Ansonsten blickt danach keiner durch." und „Außerdem ist der Zeitraum
   zu erfassen." Tag (bei mehreren Tagen von bis), Uhrzeit von bis, Firma, Team, bei Teilterminen die Zahl der Connects. */
function TerminZelle({ a }: { a: any }) {
  const c = a.connect_termine ?? {};
  const t = a.termin;
  const ab = c.n > 0 ? c.ab : t?.tag ? String(t.tag).slice(0, 10) : null;
  if (!ab) return <span className="leise">kein Termin</span>;
  const bis = c.n > 0 ? (c.letzter && c.letzter > (c.bis ?? '') ? c.letzter : c.bis) : t?.letzter_tag;
  const zeit = c.n > 0 ? (c.ganztags ? 'ganztags' : c.von ? `${c.von} bis ${c.bis_uhr ?? ''} Uhr` : '')
    : t ? `${String(t.beginn).slice(0, 5)} bis ${t.ende ?? ''} Uhr` : '';
  return (
    <>
      <div style={{ whiteSpace: 'nowrap' }}><b>{tagLang(ab)}</b></div>
      {bis && bis !== ab && <div style={{ whiteSpace: 'nowrap' }}>bis {tagLang(bis)}</div>}
      {zeit && <div>{zeit}</div>}
      {t ? <div style={{ marginTop: 2 }}>{t.firma}<div className="leise">{t.team}</div></div> : <div className="leise">ohne Firma</div>}
      {c.n > 0 && c.n < c.alle && <div className="leise">{c.n} von {c.alle} Connects</div>}
    </>
  );
}

function Ne4Info({ a }: { a: any }) {
  const x = a.letzte_info;
  if (!x) return <span className="leise">nichts vermerkt</span>;
  const was = KONTAKT[x.art] ?? x.art;
  const erg = x.ergebnis && ERGEBNIS[x.ergebnis] && ERGEBNIS[x.ergebnis] !== was ? `: ${ERGEBNIS[x.ergebnis]}` : '';
  return (
    <div className="auftragsinfo" style={{ maxWidth: 340 }} title={x.text ?? ''}>
      <b style={{ display: 'block' }}>{was}{erg}</b>{x.text ? <span>{ohnePunkte(String(x.text).length > 140 ? String(x.text).slice(0, 140) + ' …' : String(x.text))}</span> : null}
      <div className="leise" style={{ fontSize: 11 }}>{zeitpunkt(x.wann)}{x.wer ? `, ${String(x.wer).trim().split(/\s+/)[0]}` : ''}{a.info_anzahl > 1 ? `, ${a.info_anzahl} Einträge` : ''}, {a.mails} Mails, {a.anrufe} Anrufe</div>
    </div>
  );
}

export function Zustandschip({ z, inArbeit }: { z: string; inArbeit?: string | null }) {
  /* .592: „In Arbeit", sobald das Team angefangen hat; der Auftrag bleibt dabei terminiert. */
  if (z === 'terminiert' && inArbeit) return <span className="marke-zustand z-laufend" title={`seit ${new Date(inArbeit).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}`}>In Arbeit</span>;
  return <span className={`marke-zustand ${ZUSTAND_KLASSE[z] ?? ''}`}>{ZUSTAND[z] ?? z}</span>;
}

/* ================================================================== Aufträge */

/* DIE KLS-ID ZUM KOPIEREN (.515). Marvin: „Die KLS-ID muss zum Kopieren sein." */
/* .598: DIE KLS-ID WIE IN NE3 (Kopierbar: Knopf mit „kopieren" beim Drüberfahren und „KLS-ID kopiert" an der Stelle).
   Marvin, 02.11.2026: „KLS-ID markieren, kopieren. Falsch, immer noch falsch. Guck dir das bei NE3 an." Der Klick
   kopiert nur und öffnet nicht die Zeile dahinter. */
export function KlsKopie({ kls }: { kls: string | null | undefined }) {
  if (!kls) return <span className="leise">ohne KLS</span>;
  return <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-block' }}><Kopierbar wert={kls} was="KLS-ID" /></span>;
}

/* DER WARTEGRUND UND DER NE4-STAND ALS TEXT in der Zeile (.515). Marvin: „Ich sehe den Wartegrund nicht so richtig." */
export function BulkZeile({ b, kurz }: { b: any; kurz?: boolean }) {
  if (!b?.zustand) return null;
  /* .521: Marvin: „Das heißt, der Wartegrund, wir können keinen Termin machen,
     ist das richtig?" Ja: „NE4 warten" heißt, das Portal lässt für dieses
     Haus keinen Connect-Termin zu; der Grund steht dabei. „NE4 wählbar"
     heißt, Termine gehen. Oben in der Akte nur das (kurz), die Einzelheiten
     stehen im GBGS-Kasten. */
  /* .619: AUS DEM BULK-PORTAL VERSCHWUNDEN. Marvin, 03.11.2026: „Vielleicht wurden sie schon wieder rausgenommen."
     Der Stand darunter ist der letzte gesehene; der Vermerk mit dem Zeitpunkt steht im Verlauf des Hauses. */
  if (b.weg_am) return <div className="klein" style={{ color: 'var(--rot, #a33)' }}><b>Im Bulk-Portal nicht mehr da</b> seit {datum(b.weg_am)}, bei der Telekom klären</div>;
  return (
    <div className="klein">
      <span style={{ color: b.zustand === 'wartet' ? 'var(--rot, #a33)' : b.zustand === 'waehlbar' ? 'var(--erledigt, #0f6e56)' : undefined }}>
        <b>NE4 {b.zustand === 'waehlbar' ? 'wählbar' : b.zustand === 'wartet' ? 'warten' : b.zustand}</b>
        {b.zustand === 'wartet' && b.grund_text ? `: ${b.grund_text}` : ''}
        {b.zustand === 'wartet' ? ', kein Termin im Portal möglich' : b.zustand === 'waehlbar' ? ', Termin möglich' : ''}
      </span>
      {b.gebaut ? `, ${b.gebaut} von ${b.dosen} Connects gebaut` : ''}
      {!kurz && b.auskundung_neu ? ', neue Auskundung' : ''}
      {b.wartegrund && <div style={{ color: 'var(--warnung, #b45309)' }}><b>Wartegrund:</b> {b.wartegrund_text || b.wartegrund}{!kurz && b.wartegrund_kommentar ? ` („${b.wartegrund_kommentar}“)` : ''}</div>}
    </div>
  );
}

const NE4_SORTIERUNG: Record<string, string> = { geaendert: 'zuletzt geändert', termin: 'Termin: früheste zuerst', anschrift: 'Ort und Anschrift', kls: 'KLS-ID' };

export function Ne4Auftraege({ ich, oeffne }: { ich: Anmelder; oeffne?: string | null }) {
  const [d, setD] = useState<any>(null);
  const [zustand, setZustand] = useState('offen');
  const [gbgs, setGbgs] = useState('');
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [offen, setOffen] = useState<string | null>(oeffne ?? offenAusAdresse('ne4_auftraege'));
  useEffect(() => { if (oeffne) setOffen(oeffne); }, [oeffne]);
  /* .597: das offene Haus steht in der Adresse, ein Neuladen (Update, Wartung) öffnet es wieder. */
  useEffect(() => { offenInAdresse('ne4_auftraege', offen); }, [offen]);
  const [importOffen, setImportOffen] = useState(false);
  const [neuOffen, setNeuOffen] = useState(false);
  const [importe, setImporte] = useState<any[]>([]);
  const laden = () => {
    const p = new URLSearchParams({ zustand }); if (suche.trim()) p.set('suche', suche.trim()); if (gbgs) p.set('gbgs', gbgs);
    return Promise.all([api.ne4Auftraege(p).then(setD), api.ne4Importe().then(setImporte).catch(() => setImporte([]))]).catch((f) => setFehler(ausFehler(f)));
  };
  const sucheVerzoegert = useVerzoegert(suche, 220);
  useEffect(() => { laden(); }, [zustand, gbgs, sucheVerzoegert]); // eslint-disable-line react-hooks/exhaustive-deps
  useFrisch(laden);
  /* .556: NEU AUS DEM GBGS LESEN, mit Stand. Läuft das Lesen, wird alle 5 Sekunden nachgesehen. */
  const [hinweisGbgs, setHinweisGbgs] = useState<string | null>(null);
  const liestGbgs = !!d?.gbgs?.laeuft;
  useEffect(() => {
    if (!liestGbgs) return;
    const t = setInterval(() => { void laden(); }, 5000);
    return () => clearInterval(t);
  }, [liestGbgs]); // eslint-disable-line react-hooks/exhaustive-deps
  const gbgsLesen = async () => {
    setHinweisGbgs(null);
    try { const r = await api.ne4GbgsLesen(); setHinweisGbgs(r.klartext); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
  };
  const z = d?.zahlen;
  /* FILTER IN DER TITELLEISTE (.520). Marvin: „ich will die Titelleiste
     sozusagen filtern können nach Eigentümer. Gleiche Verwaltung. Ja, oder
     nach Adresse." Eigentümer und Ort filtern die geladene Liste. */
  const [wer, setWer] = useState('');
  const [ort, setOrt] = useState('');
  /* .603: nur Häuser, bei denen die Connects im Portal nicht zu den Einheiten passen. */
  const [nurAbweichung, setNurAbweichung] = useState(false);
  const werVon = (a: any) => a.eigentuemer_org || [a.vorname, a.name].filter(Boolean).join(' ') || '';
  const alleZeilen: any[] = d?.zeilen ?? [];
  const wers = [...new Set<string>(alleZeilen.map(werVon).filter(Boolean))].sort((x, y) => x.localeCompare(y, 'de'));
  const orte = [...new Set<string>(alleZeilen.map((a) => String(a.ort ?? '')).filter(Boolean))].sort((x, y) => x.localeCompare(y, 'de'));
  /* .589: ZURÜCK STEHT MAN AUF DEM AUFTRAG. Marvin, 02.11.2026: „wenn du wieder auf die Aufträge gehst,
     zurückgehst oder terminiert hast, dann soll es nicht auf alle Aufträge springen, sondern er soll auf dem
     Auftrag stehen bleiben, wo er war ... momentan sucht man dann den Auftrag noch ... dann ist der Auftrag weg."
     Der zuletzt offene Auftrag bleibt in der Liste, auch wenn er nach dem Terminieren nicht mehr zur Auswahl
     passt (oben, markiert), die Liste rollt zu ihm, und Filter und Suche bleiben, wie sie waren. */
  const [zuletzt, setZuletzt] = useState<string | null>(null);
  const [zuletztZeile, setZuletztZeile] = useState<any | null>(null);
  useEffect(() => {
    if (!zuletzt || !d) return;
    if (alleZeilen.some((a) => a.id === zuletzt)) { setZuletztZeile(null); return; }
    const kls = zuletztZeile?.kls_id ?? null;
    api.ne4Auftrag(zuletzt).then((k: any) => api.ne4Auftraege(new URLSearchParams({ zustand: 'alle', suche: k.kls_id ?? kls ?? '' })))
      .then((r: any) => setZuletztZeile((r?.zeilen ?? []).find((a: any) => a.id === zuletzt) ?? null)).catch(() => null);
  }, [zuletzt, d]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sortierung, setSortierung] = useState<string>(() => { try { return localStorage.getItem('ne4_sortierung') || 'geaendert'; } catch { return 'geaendert'; } });
  useEffect(() => { try { localStorage.setItem('ne4_sortierung', sortierung); } catch { /* egal */ } }, [sortierung]);
  const sortiert = (l: any[]) => {
    if (sortierung === 'geaendert') return l;
    const k = (a: any) => sortierung === 'anschrift' ? `${a.ort ?? ''} ${a.strasse ?? ''} ${String(a.hausnummer ?? '').padStart(6, '0')}`
      : sortierung === 'kls' ? String(a.kls_id ?? '').padStart(12, '0')
      : sortierung === 'termin' ? (a.termin?.tag ?? a.connect_termine?.ab ?? '9999') : '';
    return [...l].sort((x, y) => (x._gehalten ? -1 : y._gehalten ? 1 : k(x).localeCompare(k(y), 'de')));
  };
  const gefiltert = alleZeilen.filter((a) => (!wer || werVon(a) === wer) && (!ort || a.ort === ort) && (!nurAbweichung || connectAbgleich(a, a.bulk_kurz?.dosen).passt === false));
  const zeilen = zuletztZeile && !gefiltert.some((a) => a.id === zuletztZeile.id) ? [{ ...zuletztZeile, _gehalten: true }, ...gefiltert] : gefiltert;
  useEffect(() => {
    if (!zuletzt || offen) return;
    const t = setTimeout(() => { document.querySelector(`tr[data-ne4-id="${zuletzt}"]`)?.scrollIntoView({ block: 'center' }); }, 60);
    return () => clearTimeout(t);
  }, [zuletzt, offen, d, zuletztZeile]);
  /* DIE AKTE ALS SEITE (.520): statt der Liste, mit „zurück". */
  if (offen) return <AuftragsKarte ich={ich} id={offen} seite zu={() => { setZuletzt(offen); setOffen(null); laden(); }} geaendert={laden} wechsel={setOffen} />;
  return (
    <>
      {/* .609: DER AUFBAU WIE DIE NE3-AUFTRAGSLISTE. Marvin, 03.11.2026: „Kannst du hier bitte den Aufbau identisch
          machen, dass die Reihen auch so stehen, wie sie sonst auch standen? ... wir arbeiten in zwei Systemen ...
          bitte angleichen alles." Oben die Zustände als Knöpfe mit Zahl, rechts Eigentümer und Ort; darunter „Was
          ansteht" (GBGS); dann die Suche für sich; dann „x von y" mit Sortierung; die Spalten in der Reihenfolge der
          NE3-Liste: Zustand, KLS, Baustelle, Einheiten (statt Auftragstyp), Kunde, Auftragsinfo, Termin (statt Gewerke). */}
      <Kopf titel="Aufträge" weg={z ? `NE4, ${z.alle} Aufträge, ${z.offen} offen, ${z.terminiert} terminiert` : 'NE4'}
            rechts={darf(ich, 'ne4.auftraege.pflegen') ? <button type="button" className="knopf" onClick={() => setNeuOffen(true)}>Auftrag anlegen</button> : undefined} />
      <div className="inhalt">
      {neuOffen && <NeuDialog zu={() => setNeuOffen(false)} fertig={(k) => { setNeuOffen(false); laden(); setOffen(k.id); }} />}
      <Fehlerkasten lage={fehler} />
      {/* .617: DIESELBE KLAPPE WIE IN NE3 (teile/Filterklappe.tsx): auf dem Telefon ein Knopf mit der Auswahl, die Reihen
          darunter erst, wenn er offen ist. Marvin, 03.11.2026: „Bitte auch bei NE4 hier einbauen ... dass das Grundsystem
          identisch ist." */}
      <Filterklappe worte={[
        zustand !== 'alle' ? (zustand === 'offen' ? 'Offen' : ZUSTAND[zustand] ?? zustand) : '',
        wer, ort,
        gbgs ? ({ waehlbar: 'NE4 wählbar', wartet: 'NE4 wartet', wartegrund: 'Wartegrund', auskundung_neu: 'Neue Auskundung', weg: 'Aus dem Bulk verschwunden' } as Record<string, string>)[gbgs] ?? gbgs : '',
        nurAbweichung ? 'Connects passen nicht' : '',
      ].filter(Boolean)}>
      <div className="reihe reiterreihe">
        {[['alle', 'Alle', z?.alle], ['offen', 'Offen', z?.offen], ...Object.entries(ZUSTAND).map(([k, v]) => [k, v, z?.[k]])].map(([k, v, n]: any) => (
          <button key={k} type="button" className={'knopf filter' + (zustand === k ? ' gewaehlt' : '')} aria-pressed={zustand === k}
                  onClick={() => setZustand(zustand === k && k !== 'alle' ? 'alle' : k)}>
            {v}{n != null && <span className="filterzahl">{n}</span>}
          </button>
        ))}
        <div className="wachsen" />
        <select value={wer} onChange={(e) => setWer(e.target.value)} title="Eigentümer / Verwaltung" style={{ maxWidth: 240 }}>
          <option value="">Alle Eigentümer</option>{wers.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={ort} onChange={(e) => setOrt(e.target.value)} title="Ort" style={{ maxWidth: 180 }}>
          <option value="">Alle Orte</option>{orte.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div className="reihe reiterreihe ansteht">
        <span className="klein leise anstehtkopf">Was ansteht</span>
        {([['waehlbar', 'NE4 wählbar', z?.gbgs_waehlbar, 'f-geld', 'Das Bulk-Portal lässt Termine zu.'],
           ['wartet', 'NE4 wartet', z?.gbgs_wartet, 'f-warte', 'Das Bulk-Portal lässt keinen Termin zu; der Grund steht in der Zeile.'],
           ['wartegrund', 'Wartegrund', z?.gbgs_wartegrund, 'f-warte', 'Im Bulk-Portal steht ein Wartegrund.'],
           ['auskundung_neu', 'Neue Auskundung', z?.gbgs_auskundung_neu, '', 'Im Portal wurde eine neue Auskundung angestoßen.'],
           ['weg', 'Aus dem Bulk verschwunden', z?.gbgs_weg, 'f-warte', 'Der Gebäudeauftrag steht nicht mehr im Bulk-Portal; der Vermerk mit dem Zeitpunkt steht im Verlauf des Hauses.']] as const)
          .filter(([k, , n]) => (n ?? 0) > 0 || gbgs === k).map(([k, v, n, farbe, satz]) => (
          <button key={k} type="button" title={satz} className={'knopf filter' + (gbgs === k ? ' gewaehlt' : '') + (farbe ? ' ' + farbe : '')}
                  aria-pressed={gbgs === k} onClick={() => setGbgs(gbgs === k ? '' : k)}>
            {v}<span className="filterzahl">{n ?? 0}</span>
          </button>
        ))}
        <button type="button" title="Connects im Bulk-Portal und Einheiten laut Kunde oder Auskundung stimmen nicht überein"
                className={'knopf filter' + (nurAbweichung ? ' gewaehlt' : '')} aria-pressed={nurAbweichung} onClick={() => setNurAbweichung(!nurAbweichung)}>
          Connects passen nicht<span className="filterzahl">{alleZeilen.filter((a) => connectAbgleich(a, a.bulk_kurz?.dosen).passt === false).length}</span>
        </button>
        {(gbgs || nurAbweichung) && <button type="button" className="knopf klein" onClick={() => { setGbgs(''); setNurAbweichung(false); }}>zurücksetzen</button>}
      </div>
      </Filterklappe>
      <div className="reihe" style={{ marginBottom: 10 }}>
        <Suchfeld wert={suche} setze={(w) => { if (w && !suche) { setZustand('alle'); setGbgs(''); } setSuche(w); }} mehr="Name, Eigentümer" breit={520} />
      </div>
      <div className="listenkopf">
        <span>{d ? `${zeilen.length} von ${alleZeilen.length}` : 'wird geladen'}</span>
        <span className="leise klein" style={{ marginLeft: 14 }}>
          {liestGbgs ? 'Liest gerade aus dem GBGS (Bulk-Portal) …'
            : d?.gbgs?.stand_am ? <>Stand aus dem GBGS: <b>{zeitpunkt(d.gbgs.stand_am)} Uhr</b></> : 'Aus dem GBGS noch nicht gelesen.'}
        </span>
        {darf(ich, 'ne4.auftraege.pflegen') && (
          <button type="button" className="knopf klein" style={{ marginLeft: 8 }} disabled={liestGbgs} onClick={gbgsLesen}
                  title="Liest das Bulk-Portal neu (NE4-Zustand, Auskundung, Dosen) und zieht neue Häuser nach. Im Portal wird nichts geändert.">
            {liestGbgs ? 'Liest …' : 'Aus GBGS neu lesen'}
          </button>
        )}
        {hinweisGbgs && liestGbgs && <span className="leise klein" style={{ marginLeft: 8 }}>{hinweisGbgs}</span>}
        <span className="wachsen" />
        <a className="knopf klein" href="/api/ne4/auftraege/gbgs-wartend.csv?was=wartet" target="_blank" rel="noreferrer" title="Alle Häuser, bei denen NE4 im Bulk-Portal auf „warten“ steht, mit Grund, als CSV für die Telekom.">CSV: NE4 wartet</a>
        <a className="knopf klein" href="/api/ne4/auftraege/gbgs-wartend.csv?was=auskundung_neu" target="_blank" rel="noreferrer" title="Alle Häuser mit neu angestoßener Auskundung, als CSV." style={{ marginRight: 12 }}>CSV: neue Auskundung</a>
        <span className="sortierwahl">
          <label htmlFor="ne4sortierung">Sortiert nach</label>
          <select id="ne4sortierung" value={sortierung} onChange={(e) => setSortierung(e.target.value)}>
            {Object.entries(NE4_SORTIERUNG).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </span>
      </div>
      <div className="karte">
        {d && !zeilen.length && <div className="leise klein" style={{ padding: 14 }}>Keine Aufträge in dieser Auswahl.{z?.alle === 0 && ' Der erste Import legt sie an.'}</div>}
        {!!zeilen.length && (
          <div className="rollt">
          <table className="liste auftragsliste">
            <thead><tr>
              <th style={{ width: 150 }}>Zustand</th><th style={{ width: 96 }}>KLS</th><th style={{ width: 200 }}>Baustelle</th>
              <th style={{ width: 110 }}>Einheiten</th><th style={{ width: 200 }}>Kunde</th><th>Auftragsinfo</th><th style={{ width: 170 }}>Termin</th>
            </tr></thead>
            <tbody>
              {sortiert(zeilen).map((a: any) => (
                <tr key={a.id} data-ne4-id={a.id} className={a.id === zuletzt ? 'gewaehlt' : undefined} onClick={() => setOffen(a.id)}>
                  <td>
                    <Zustandschip z={a.zustand} />{a.rueckgabe_vorgemerkt && <div className="klein" style={{ color: 'var(--rot, #a33)' }}>zur Rückgabe</div>}
                    {a._gehalten && <div className="klein" style={{ color: 'var(--akzent)' }}>eben bearbeitet, passt nicht mehr zur Auswahl</div>}
                    <BulkZeile b={a.bulk_kurz} kurz />
                  </td>
                  <td><KlsKopie kls={a.kls_id} />{a.art !== 'GEB' && <div className="klein leise">{a.art === 'HC' ? 'Home Connect' : a.art === 'GWV' ? 'Gewerksvorbereitung' : a.art}</div>}</td>
                  <td><b>{a.strasse} {a.hausnummer}</b><div className="klein leise">{a.plz} {a.ort}</div></td>
                  <td className="klein" style={{ whiteSpace: 'nowrap' }}>
                    <div>{a.wohneinheiten ?? 0} WE, {a.ge ?? 0} GE, {a.sl ?? 0} SL</div>
                    {connectAbgleich(a, a.bulk_kurz?.dosen).passt === false && <div className="fehlertext" title="Connects im Bulk-Portal und Einheiten laut Kunde (oder Auskundung) stimmen nicht überein">Portal: {a.bulk_kurz.dosen} Connects</div>}
                    {a.bulk_kurz?.gebaut ? <div className="leise">{a.bulk_kurz.gebaut} gebaut</div> : null}
                  </td>
                  <td>
                    {a.art === 'GEB' && a.eigentuemer_org && <div>{a.eigentuemer_org}</div>}
                    <div className={a.art === 'GEB' && a.eigentuemer_org ? 'klein' : ''}>{[a.vorname, a.name].filter(Boolean).join(' ') || <span className="leise">ohne Namen</span>}</div>
                    <div className="klein leise">{a.mobil || a.telefon || ''}</div>
                  </td>
                  <td className="klein"><Ne4Info a={a} /></td>
                  <td className="klein"><TerminZelle a={a} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {/* DER EXCEL-IMPORT IST EIN NEBENWEG (.537). Marvin: „das sind nur
          Aufträge, nicht Import. Wir lesen ja über GBGS ein ... Excel
          importieren, weil das ist ja nur ein optionaler Punkt, wenn es da
          mal greift." Der Knopf steht deshalb nicht mehr oben im Kopf,
          sondern klein bei den Importen, ganz unten. */}
      {(darf(ich, 'ne4.import') || !!importe.length) && (
        <Karte titel="Excel-Importe" rechts={darf(ich, 'ne4.import') ? <button type="button" className="knopf klein" onClick={() => setImportOffen(true)}>Excel importieren</button> : undefined}>
          {!importe.length && <div className="klein leise">Die Aufträge kommen aus dem GBGS. Eine Excel-Liste der Telekom ist der Nebenweg, falls einmal eine kommt.</div>}
          {!!importe.length && (
          <table className="liste">
            <thead><tr><th>Wann</th><th>Datei</th><th className="zahl">Zeilen</th><th className="zahl">Neu</th><th className="zahl">Aktualisiert</th><th className="zahl">Übersprungen</th><th>Wer</th></tr></thead>
            <tbody>
              {importe.map((i) => (
                <tr key={i.id} style={{ cursor: 'default' }}>
                  <td className="klein">{zeitpunkt(i.created_at)}</td><td>{i.dateiname}{!!(i.fehler ?? []).length && <div className="klein leise">{i.fehler.length} Hinweise: {i.fehler.slice(0, 2).join('; ')}</div>}</td>
                  <td className="zahl">{i.zeilen}</td><td className="zahl">{i.neu}</td><td className="zahl">{i.aktualisiert}</td><td className="zahl">{i.uebersprungen}</td><td className="klein">{i.benutzer ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </Karte>
      )}
      {importOffen && <ImportDialog zu={() => setImportOffen(false)} fertig={() => { setImportOffen(false); laden(); }} />}
      </div>
    </>
  );
}

/* ==================================================================== Import */

function ImportDialog({ zu, fertig }: { zu: () => void; fertig: () => void }) {
  const [datei, setDatei] = useState<File | null>(null);
  const [v, setV] = useState<any>(null);
  const [felder, setFelder] = useState<any[]>([]);
  const [zuordnung, setZuordnung] = useState<Record<string, string>>({});
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [ergebnis, setErgebnis] = useState<any>(null);
  useEffect(() => { api.ne4ImportFelder().then((r) => setFelder(r.felder)).catch(() => setFelder([])); }, []);
  async function vorschau(f: File) {
    setDatei(f); setLaeuft(true); setFehler(null); setV(null);
    try { const r = await api.ne4ImportVorschau(f); setV(r); setZuordnung(r.zuordnung); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  async function einlesen() {
    if (!datei) return;
    setLaeuft(true); setFehler(null);
    try { setErgebnis(await api.ne4Import(datei, zuordnung)); } catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  const klsDa = Object.values(zuordnung).includes('kls_id');
  return (
    <Dialog titel="Excel der Telekom importieren" breite={900} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {ergebnis ? (
        <>
          <div className="hinweis erledigt">{ergebnis.neu} Aufträge neu, {ergebnis.aktualisiert} aktualisiert, {ergebnis.uebersprungen} übersprungen (von {ergebnis.zeilen} Zeilen). Koordinaten, Gebiete und Mails folgen mit dem Stundenlauf.</div>
          {!!ergebnis.fehler?.length && <div className="hinweis warnung"><b>Hinweise:</b><ul className="klein">{ergebnis.fehler.slice(0, 30).map((f: string, i: number) => <li key={i}>{f}</li>)}</ul></div>}
          <div className="reihe"><button type="button" className="knopf haupt" onClick={fertig}>Fertig</button></div>
        </>
      ) : (
        <>
          <p className="klein leise" style={{ marginTop: 0 }}>Die Datei wird gelesen, die Spalten werden vorgeschlagen und hier bestätigt. Je KLS-ID entsteht ein Auftrag; eine bekannte KLS-ID wird aktualisiert. Unbekannte Spalten gehen als Zusatz mit.</p>
          <div className="feld"><label>Excel-Datei (.xlsx)</label><input type="file" accept=".xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) vorschau(f); }} /></div>
          {v && (
            <>
              <p className="klein">{v.gesamt} Datenzeilen. {v.gelernt.length ? `${v.gelernt.length} Spalten aus der gemerkten Leseregel.` : 'Erste Liste: bitte die Zuordnung prüfen.'}{v.kls_fehlt && <b> Keine Spalte für die KLS-ID erkannt, bitte wählen.</b>}</p>
              <table className="liste">
                <thead><tr><th>Spalte in der Excel</th><th>Feld</th><th>Beispiel</th></tr></thead>
                <tbody>
                  {v.kopf.map((k: string, i: number) => (
                    <tr key={k} style={{ cursor: 'default' }}>
                      <td><b>{k}</b></td>
                      <td><select value={zuordnung[k] ?? 'weg'} onChange={(e) => setZuordnung({ ...zuordnung, [k]: e.target.value })}>
                        {felder.map((f) => <option key={f.feld} value={f.feld}>{f.titel}</option>)}
                      </select></td>
                      <td className="klein leise">{v.zeilen.map((z: string[]) => z[i]).filter(Boolean).slice(0, 3).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="reihe" style={{ marginTop: 12 }}>
                <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
                <button type="button" className="knopf haupt" disabled={laeuft || !klsDa} onClick={einlesen}>{v.gesamt} Zeilen einlesen</button>
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}

/* ============================================================ Auftragskarte */
/* Kacheln auf Modulebene (.521), damit die Eingabefelder darin beim Tippen den Fokus behalten. */
function KachelGeb({ children }: { children: any }) { return <div className="karte" style={{ padding: 12, margin: 0 }}>{children}</div>; }
function KeineKachel({ children }: { children: any }) { return <>{children}</>; }

type Frage = null | 'status' | 'telekom' | 'rueckruf' | 'mail' | 'ablehnen' | 'kalender' | 'notiz' | 'bearbeiten' | 'rueckgabe' | 'absagen' | 'nicht_erreicht' | 'block' | 'block_absagen' | 'aushang' | 'bestaetigung' | 'erinnerung';
/* DIE AKTE ALS SEITE (.520). Marvin, 28.10.2026: „ich möchte das so haben
   wie bei NE3, dass ich auf einen Auftrag klicke und das kommt auf der
   normalen Seite in dem Menü mit den Aufträgen und nicht hier als Push, als
   Pop-up." `seite` zeichnet die Karte als Seite mit Kopf und „zurück";
   `wechsel` springt in der Verwaltung von Haus zu Haus. */
export function AuftragsKarte({ ich, id, zu, geaendert, start, gewaehltVorher, wechsel, seite }: { ich: Anmelder; id: string; zu: () => void; geaendert: () => void; start?: Frage; gewaehltVorher?: { welche: 'mobil' | 'telefon'; nummer: string } | null; wechsel?: (id: string) => void; seite?: boolean }) {
  const [a, setA] = useState<any>(null);
  const [v, setV] = useState<any>(null);
  /* .609: REITER WIE IN DER NE3-AKTE (Übersicht, Connects, Dateien); der Reiter bleibt beim Neuladen stehen. */
  const [reiter, setReiterRoh] = useState<HausReiter>(() => { try { return (sessionStorage.getItem('ne4_reiter') as HausReiter) || 'uebersicht'; } catch { return 'uebersicht'; } });
  const setReiter = (r: HausReiter) => { setReiterRoh(r); try { sessionStorage.setItem('ne4_reiter', r); } catch { /* egal */ } };
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [frage, setFrage] = useState<Frage>(start ?? null);
  /* DIE ZULETZT GEWÄHLTE NUMMER, damit „Nicht erreicht" weiß, bei welcher. */
  const [gewaehlt, setGewaehlt] = useState<{ welche: 'mobil' | 'telefon'; nummer: string } | null>(gewaehltVorher ?? null);
  const [text, setText] = useState('');
  const [wann, setWann] = useState('');
  const [mail, setMail] = useState('');
  const [eintrag, setEintrag] = useState('');
  /* .532: Eintrag im Fenster, berichtigen und löschen wie bei NE3. */
  const [eintragOffen, setEintragOffen] = useState(false);
  const [eintragFirma, setEintragFirma] = useState(false);
  const [berichtige, setBerichtigeRoh] = useState<Infozeile | null>(null);
  const [berichtigeText, setBerichtigeText] = useState('');
  const setBerichtige = (e: Infozeile | null) => { setBerichtigeRoh(e); setBerichtigeText(e ? String(e.text ?? '') : ''); };
  const [loesche, setLoesche] = useState<Infozeile | null>(null);
  const [wvWann, setWvWann] = useState('');
  const [wvGrund, setWvGrund] = useState('');
  const kannAnrufen = darf(ich, 'ne4.anrufen');
  const kannPflegen = darf(ich, 'ne4.auftraege.pflegen');
  useEffect(() => { setEintrag(''); setWvWann(''); setWvGrund(''); setMeldung(null); setFehler(null); }, [id]);
  const laden = async () => {
    try {
      const k = await api.ne4Auftrag(id); setA(k);
      if (kannAnrufen && ['import', 'angeschrieben', 'wartet'].includes(k.zustand)) setV(await api.ne4Vorschlaege(id)); else setV(null);
    } catch (f) { setFehler(ausFehler(f)); }
  };
  useEffect(() => { laden(); }, [id]);
  useFrisch(laden);

  async function tu(f: () => Promise<any>, ok?: string) {
    setLaeuft(true); setFehler(null); setMeldung(null);
    try { const r = await f(); if (r && r.id) setA(r); else await laden(); if (ok) setMeldung(ok); setFrage(null); setText(''); setWann(''); geaendert(); if (r?.bestaetigung && !r.bestaetigung.gesendet && !r.bestaetigung.vorschau) setMeldung(`Termin gebucht. Bestätigung nicht per Mail: ${r.bestaetigung.grund}.`); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  const buchen = (p: any) => tu(() => api.ne4TerminBuchen(id, p.teamtag_id, p.beginn), 'Termin gebucht, Bestätigung geht per Mail.');
  const link = a?.link;
  const kopieren = (t: string) => { try { navigator.clipboard.writeText(t); setMeldung('In der Zwischenablage.'); } catch { setMeldung(t); } };

  const [linkKopiert, setLinkKopiert] = useState<string | null>(null);
  const [mailOffen, setMailOffen] = useState(false);
  if (!a) return seite
    ? <><div className="kopf"><button type="button" className="knopf" onClick={zu}>&larr; zurück</button><h1>Auftrag</h1></div><div className="inhalt"><Fehlerkasten lage={fehler} /><div className="leise klein">Lade …</div></div></>
    : <Dialog titel="Auftrag" breite={860} laeuft zu={zu}><Fehlerkasten lage={fehler} /><div className="leise klein">Lade …</div></Dialog>;
  const offen = ['import', 'angeschrieben', 'wartet'].includes(a.zustand);
  /* .513: DER KONTAKTBLOCK (Kunde, Wahl, nach dem Gespräch). Beim Gebäude
     steht er links oben in der breiten Hauskarte, sonst unter den Vorschlägen. */
  /* .521: beim Gebäude jeder Block eine Kachel (Eigentümer und Gespräch, Wiedervorlage, Auftragsinfo). */
  const Kachel = a.art === 'GEB' ? KachelGeb : KeineKachel;
  const kontaktBlockMit = (extra?: any) => (<>
      <Kachel>
      <div style={{ display: 'grid', gridTemplateColumns: a.art === 'GEB' ? '1fr' : '1fr 1fr', gap: 12, marginTop: a.art === 'GEB' ? 0 : 10 }} className="ne4-karte-spalten">
        <div>
          <b>{a.art === 'GEB' ? 'Eigentümer / Ansprechpartner' : 'Kunde'}</b>
          {a.art === 'GEB' && <div className="klein">{a.eigentuemer_org ? <b>{a.eigentuemer_org}</b> : <span className="leise">keine Organisation</span>}{[a.vorname, a.name].filter(Boolean).length ? `, ${[a.vorname, a.name].filter(Boolean).join(' ')}` : ''}</div>}
          <div className="klein">{a.strasse} {a.hausnummer}, {a.plz} {a.ort}</div>
          {/* .486: auch hier wird jede Wahl vermerkt, sonst fehlt sie in „Anrufe je Mitarbeiter“.
              .559: DIE NUMMERN EINMAL, ANKLICKBAR. Marvin: „Dann brauche ich auch nur einmal die Rufnummern. Und zwar so, dass sie anklickbar sind." */}
          {/* .593: ANRUFEN MARKANT. Marvin, 02.11.2026: „wenn du im Auftrag drinne bist, soll es markanter sein,
              wo angerufen werden muss, dass du da draufklicken kannst". Große Knöpfe, ein Klick wählt und vermerkt. */}
          <div className="reihe ne4-anrufen" style={{ gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
            {a.mobil && <button type="button" className="knopf haupt ne4-anruf-knopf" onClick={() => waehlen(a.id, 'mobil', a.mobil).then(() => { setGewaehlt({ welche: 'mobil', nummer: a.mobil }); laden(); })}>📞 Mobil anrufen<span>{a.mobil}</span></button>}
            {a.telefon && <button type="button" className="knopf haupt ne4-anruf-knopf" onClick={() => waehlen(a.id, 'telefon', a.telefon).then(() => { setGewaehlt({ welche: 'telefon', nummer: a.telefon }); laden(); })}>📞 Festnetz anrufen<span>{a.telefon}</span></button>}
            {!a.mobil && !a.telefon && <span className="hinweis warnung" style={{ padding: '4px 8px' }}>Keine Rufnummer hinterlegt</span>}
          </div>
          {/* .559: DIE MAIL ANKLICKBAR, mit Anschrift und KLS im Betreff. */}
          <div className="klein">Mail: {a.email
            ? <a className="alslink" href={`mailto:${a.email}?subject=${encodeURIComponent(`${a.strasse ?? ''} ${a.hausnummer ?? ''}, ${a.plz ?? ''} ${a.ort ?? ''}, KLS ${a.kls_id ?? ''}`)}`}>{a.email}</a>
            : <span className="leise">keine</span>}</div>
          {a.notiz && <div className="klein" style={{ marginTop: 4 }}><b>Notiz:</b> {a.notiz}</div>}
          {/* .559: „Weitere Angaben aus der Excel" entfällt; Marvin: „braucht kein Mensch ... wir holen die Daten aus GBGS." */}
          {link && <div className="klein" style={{ marginTop: 6 }}>Buchungslink: <code style={{ fontSize: 11 }}>{link}</code> <button type="button" className="knopf klein" onClick={() => kopieren(link)}>kopieren</button></div>}
          <div className="reihe" style={{ marginTop: 8 }}>
            {kannPflegen && <button type="button" className="knopf klein" onClick={() => { setFrage('bearbeiten'); }}>Kundendaten ändern</button>}
            {kannAnrufen && <button type="button" className="knopf klein" onClick={() => setFrage('notiz')}>Notiz</button>}
          </div>
        </div>
        <div>
          {kannAnrufen && offen && (
            <>
              <b>Nach dem Gespräch</b>
              <div className="reihe" style={{ flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                <button type="button" className="knopf klein" disabled={laeuft} onClick={() => { setWann(''); setText(''); setFrage('nicht_erreicht'); }}>Nicht erreicht</button>
                <button type="button" className="knopf klein" onClick={() => setFrage('rueckruf')}>Rückruf gewünscht</button>
                <button type="button" className="knopf klein" onClick={() => { setMail(a.email ?? ''); setFrage('mail'); }}>{a.art === 'GEB' ? 'Nachfass-Mail senden' : 'Mail erneut senden'}</button>
                <button type="button" className="knopf klein" onClick={() => setFrage('ablehnen')}>Kunde lehnt ab</button>
                <button type="button" className="knopf klein" disabled={laeuft} onClick={() => tu(() => api.ne4Kontakt(id, { ergebnis: 'falsche_nummer' }), 'Falsche Nummer vermerkt.')}>Falsche Nummer</button>
              </div>
              {a.rueckgabe_vorgemerkt && <div className="reihe" style={{ marginTop: 8 }}><button type="button" className="knopf klein" onClick={() => setFrage('rueckgabe')}>Rückgabe an die Telekom bestätigen</button></div>}
            </>
          )}
          {kannAnrufen && a.zustand === 'terminiert' && a.termin && a.art !== 'GEB' && (
            <div className="reihe" style={{ flexWrap: 'wrap', gap: 6 }}>
              <button type="button" className="knopf klein" onClick={() => setFrage('kalender')}>Termin verschieben</button>
              <button type="button" className="knopf klein" onClick={() => setFrage('absagen')}>Termin absagen</button>
            </div>
          )}
          {kannPflegen && a.zustand === 'zurueckgegeben' && (
            <div className="reihe"><button type="button" className="knopf klein" disabled={laeuft} onClick={() => tu(() => api.ne4Wiederaufnehmen(id), 'Wieder aufgenommen.')}>Wieder aufnehmen</button></div>
          )}
          {!!a.gbgs?.length && <div className="klein leise" style={{ marginTop: 8 }}>GBGS: {a.gbgs.filter((g: any) => ['hand', 'wartet', 'laeuft'].includes(g.zustand)).length} offen, {a.gbgs.filter((g: any) => g.zustand === 'erledigt').length} eingetragen</div>}
          {!!a.positionen?.length && <div className="klein" style={{ marginTop: 6 }}><b>Positionen:</b> {a.positionen.map((p: any) => `${p.nummer} x${p.menge}`).join(', ')}</div>}
          {!!a.dateien?.length && a.art !== 'GEB' && <div className="klein" style={{ marginTop: 6 }}><b>Dateien:</b> {a.dateien.map((d: any) => <a key={d.id} className="alslink" href={`/api/ne4/dateien/${d.id}`} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{d.dateiname}</a>)}</div>}
        </div>
      </div>
      {/* .559: DIE VERWALTUNG IM SELBEN FELD. Marvin: „warum stehen hier unten nochmal Hausverwaltungsdaten, Eigentümer? Hausverwaltung ist dasselbe. Wir brauchen einmal sozusagen seriöse Daten." */}
      {extra && <div style={{ marginTop: 10, borderTop: '1px solid var(--rand)', paddingTop: 8 }}>{extra}</div>}
      </Kachel>
      {/* DIE WIEDERVORLAGE ALS EIGENES FELD (.520). Marvin: „Wiedervorlage als extra Feld. Ja." */}
      <Kachel>
      <div style={{ marginTop: a.art === 'GEB' ? 0 : 12 }}>
        <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <b>Wiedervorlage</b>
          {!!a.wiedervorlagen?.length && <span className="klein">{a.wiedervorlagen.map((w: any) => `${zeitpunkt(w.wann)}: ${w.grund ?? ''}`).join(', ')}</span>}
          {!a.wiedervorlagen?.length && <span className="klein leise">keine</span>}
        </div>
        {kannAnrufen && (
          <div className="reihe" style={{ gap: 6, marginTop: 4, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="feld" style={{ width: 200 }}><label>Wann</label><input type="datetime-local" value={wvWann} onChange={(e) => setWvWann(e.target.value)} /></div>
            <Text name="wv_grund" titel="Grund" wert={wvGrund} setze={setWvGrund} fehler={null} breite={320} />
            <button type="button" className="knopf klein" disabled={laeuft || !wvWann} onClick={() => tu(() => api.ne4Kontakt(id, { ergebnis: 'rueckruf', wann: new Date(wvWann).toISOString(), text: wvGrund }), 'Wiedervorlage gesetzt.')}>Wiedervorlage setzen</button>
          </div>
        )}
      </div>

      </Kachel>
      {/* DIE AUFTRAGSINFO WIE BEI NE3 (.520, .532). Marvin, 28.10.2026: „Schau
          dir das bitte wie bei N3 an mit der Auftragsinfo. Das sieht nicht so
          aus ... Du sollst das Rad nicht neu erfinden." Dieselbe Liste
          (teile/Auftragsinfoliste), berichtigen und löschen, der Eintrag im
          Fenster, keine Meldung oben. */}
      <Kachel>
      <div style={{ marginTop: a.art === 'GEB' ? 0 : 12 }}>
        <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <b>Auftragsinfo ({a.kontakte.length})</b>
          {kannAnrufen && <button type="button" className="knopf klein" disabled={laeuft} onClick={() => setEintragOffen(true)}>Eintrag schreiben</button>}
        </div>
        <Auftragsinfoliste
          zeilen={a.kontakte.map((k: any): Infozeile => ({
            id: k.id, zeitpunkt: k.wann,
            /* .609: wer einen Namen hat, steht als Mensch da (mit Bild, nur Vorname), nicht als „Marvin, System". */
            quelle: k.wer ? 'person' : 'system',
            verfasser_name: k.wer ?? null, benutzer_id: k.benutzer_id ?? null, gesicht_id: k.benutzer_id ?? null, hat_bild: !!k.hat_bild,
            bearbeitet_am: k.bearbeitet_am ?? null, bearbeitet_name: k.bearbeitet_name ?? null,
            text: k.art === 'partner'
              ? `Von der Firma: ${String(k.text ?? '')}`
              : k.art === 'notiz' && k.ergebnis === 'notiz'
              ? ohnePunkte(`${String(k.text ?? '')}${k.fuer_partner ? ', für die Firma sichtbar' : ''}`)
              : ohnePunkte([`${KONTAKT[k.art] ?? k.art}${k.ergebnis && k.art !== 'import' && k.ergebnis !== k.art ? `: ${ERGEBNIS[k.ergebnis] ?? k.ergebnis}` : ''}`, k.betreff ? `„${k.betreff}"` : null, k.text].filter(Boolean).join(', ')),
            /* .585: der Anhang, so wie er mit der Mail hinausging. */
            anhang: k.datei_id ? { href: `/api/ne4/dateien/${k.datei_id}`, name: 'Anhang öffnen' } : undefined,
          }))}
          deckel={40}
          darfEintrag={(e) => e.quelle === 'person' && a.kontakte.find((k: any) => k.id === e.id)?.art === 'notiz'
            && (darf(ich, 'ne4.auftraege.pflegen') || (!!e.benutzer_id && e.benutzer_id === ich.benutzerId))}
          aufBerichtigen={(e) => setBerichtige(e)}
          aufLoeschen={(e) => setLoesche(e)} />
        {!a.kontakte.length && <div className="klein leise">Für diesen Auftrag ist noch nichts vermerkt.</div>}
      </div>
      </Kachel>
      {eintragOffen && (
        <Dialog titel="Eintrag schreiben" breite={620} laeuft={laeuft} zu={() => setEintragOffen(false)}>
          <Text name="eintrag" titel="Text" mehrzeilig wert={eintrag} setze={setEintrag} fehler={null} />
          {/* .590: freigeben für Firma und Team (Meine Aufträge). */}
          <label className="klein" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
            <input type="checkbox" checked={eintragFirma} onChange={(e) => setEintragFirma(e.target.checked)} /> für die Firma und das Team sichtbar
          </label>
          <div className="knopfreihe" style={{ marginTop: 12 }}>
            <button type="button" className="knopf haupt" disabled={laeuft || !eintrag.trim()}
                    onClick={() => { const t = eintrag.trim(); const fp = eintragFirma; setEintrag(''); setEintragFirma(false); setEintragOffen(false); void tu(() => api.ne4Kontakt(id, { ergebnis: 'notiz', text: t, fuer_partner: fp })); }}>Speichern</button>
            <button type="button" className="knopf" onClick={() => setEintragOffen(false)}>Abbrechen</button>
          </div>
        </Dialog>
      )}
      {berichtige && (
        <Dialog titel="Eintrag berichtigen" breite={620} laeuft={laeuft} zu={() => setBerichtige(null)}
                untertitel={`${zeitpunkt(berichtige.zeitpunkt)}, ${berichtige.verfasser_name ?? 'ohne Namen'}`}>
          <p className="klein leise" style={{ marginTop: 0 }}>Verfasser und Zeitpunkt bleiben, wie sie sind. Nach dem Speichern steht in der Liste, dass dieser Eintrag berichtigt wurde, mit Ihrem Namen und dem heutigen Datum.</p>
          <Text name="text" titel="Text" mehrzeilig wert={berichtigeText} setze={setBerichtigeText} fehler={null} />
          <div className="knopfreihe" style={{ marginTop: 12 }}>
            <button type="button" className="knopf haupt" disabled={laeuft || !berichtigeText.trim() || berichtigeText === berichtige.text}
                    onClick={() => { const e = berichtige; const t = berichtigeText.trim(); setBerichtige(null); void tu(() => api.ne4KontaktAendern(e.id!, t)); }}>Speichern</button>
            <button type="button" className="knopf" onClick={() => setBerichtige(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}
      {loesche && (
        <Dialog titel="Eintrag löschen" breite={560} laeuft={laeuft} zu={() => setLoesche(null)}
                untertitel={`${zeitpunkt(loesche.zeitpunkt)}, ${loesche.verfasser_name ?? 'ohne Namen'}`}>
          <p style={{ marginTop: 0 }}><b>{loesche.text}</b></p>
          <p className="klein leise">Der Eintrag verschwindet aus der Auftragsinfo. Der Wortlaut steht im Änderungsprotokoll.</p>
          <div className="knopfreihe" style={{ marginTop: 12 }}>
            <button type="button" className="knopf haupt" disabled={laeuft} onClick={() => { const e = loesche; setLoesche(null); void tu(() => api.ne4KontaktLoeschen(e.id!)); }}>Löschen</button>
            <button type="button" className="knopf" onClick={() => setLoesche(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}

  </>);
  const titel = a.art === 'GEB' ? `${a.strasse ?? ''} ${a.hausnummer ?? ''}, ${a.plz ?? ''} ${a.ort ?? ''}` : `${[a.vorname, a.name].filter(Boolean).join(' ') || 'Kunde'}, ${a.strasse ?? ''} ${a.hausnummer ?? ''}`;
  /* .537: DIE KLS-ID STEHT EINMAL. Marvin (Telefon): „dann steht die KLS-ID
     zweimal." Im Fenster trägt der Untertitel sie, auf der Seite steht sie
     rechts zum Kopieren und der Untertitel nennt nur Eigentümer bzw. Art. */
  const untertitel = seite
    ? (a.art === 'GEB' ? (a.eigentuemer_org || 'Gebäude') : a.artname)
    : (a.art === 'GEB' ? `KLS-ID ${a.kls_id}${a.eigentuemer_org ? `, ${a.eigentuemer_org}` : ''}` : `KLS-ID ${a.kls_id}, ${a.artname}`);
  /* Kein Wrapper-Bauteil im Render (es würde bei jedem Tastendruck neu
     aufgebaut und die Eingabefelder verlören den Fokus): der Inhalt ist ein
     Fragment, die Hülle wird darum gelegt. */
  const inhalt = (
    <>
      <Fehlerkasten lage={fehler} />
      {meldung && <div className="hinweis erledigt">{meldung}</div>}
      <div className="reihe" style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {!seite && <Zustandschip z={a.zustand} />}
        {!seite && a.art === 'GEB' && <BulkZeile b={a.bulk_kurz} kurz />}
        {a.rueckgabe_vorgemerkt && <span className="hinweis warnung" style={{ padding: '2px 8px' }}>zur Rückgabe vorgemerkt</span>}
        {a.termin && a.art !== 'GEB' && <span><b>Termin:</b> {tagLang(a.termin.tag)} um {String(a.termin.beginn).slice(0, 5)} Uhr, {a.termin.team} ({a.termin.firma}), {ERGEBNIS[a.termin.quelle] ?? a.termin.quelle}</span>}
        {/* .609: der Block steht nicht mehr oben drauf, sondern in der Kachel „Gewerke". */}
        {a.termin && a.art === 'GEB' && !seite && <span><b>Block:</b> ab {tagLang(a.termin.tag)}, {a.block_tage_offen} Tag{a.block_tage_offen === 1 ? '' : 'e'}, {a.termin.team} ({a.termin.firma})</span>}
        {a.frist && <span className="klein leise">Frist {datum(a.frist)}</span>}
      </div>

      {/* DIE VORSCHLÄGE ZUERST: das ist, was der Innendienst am Telefon braucht. */}
      {/* .495: ein Haus hat Block, Wohnungen und Eigentümer-Schritte statt Terminvorschlägen. */}
      {a.art === 'GEB' && <HausBlock ich={ich} a={a} laeuft={laeuft} tu={tu} setFrage={setFrage} wechsel={wechsel} kontakt={kontaktBlockMit} reiter={seite ? reiter : undefined} />}
      {a.art === 'GEB' && frage === 'block' && <BlockDialog a={a} zu={() => setFrage(null)} fertig={(k) => { setA(k); setFrage(null); geaendert(); setMeldung(k?.bestaetigung?.gesendet ? 'Block gebucht, Bestätigung ging an den Eigentümer.' : `Block gebucht. Bestätigung nicht per Mail: ${k?.bestaetigung?.grund ?? 'unbekannt'}.`); }} />}
      {kannAnrufen && offen && a.art !== 'GEB' && (
        <div className="karte" style={{ marginTop: 10, padding: 12 }}>
          <b>Terminvorschläge</b>
          {!v && <div className="klein leise">rechne …</div>}
          {v?.hinweis && <div className="klein leise">{v.hinweis}</div>}
          {!!v?.vorschlaege?.length && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8, marginTop: 8 }}>
              {v.vorschlaege.map((p: any, i: number) => (
                <div key={i} className="karte ne4-vorschlag" style={{ padding: 10, margin: 0, borderColor: p.rang === 1 ? 'var(--bereich-farbe, var(--akzent))' : undefined }}>
                  <div><b>{tagLang(p.tag)}, {uhr(p.beginn)} Uhr</b></div>
                  <div className="klein">{p.team} ({p.firma}){p.gebiet ? `, ${p.gebiet}` : ''}</div>
                  <div className="klein leise" style={{ marginBottom: 8 }}>{p.grund}{p.belegt ? `, ${p.belegt} Termine an dem Tag` : ', erster Termin an dem Tag'}</div>
                  <button type="button" className="knopf klein haupt" disabled={laeuft} onClick={() => buchen(p)}>Buchen</button>
                </div>
              ))}
            </div>
          )}
          <div className="reihe" style={{ marginTop: 8 }}>
            <button type="button" className="knopf klein" onClick={() => setFrage('kalender')}>Anderen Platz wählen</button>
          </div>
        </div>
      )}

      {a.art !== 'GEB' && kontaktBlockMit()}
      {frage === 'nicht_erreicht' && (
        <Nachfrage titel="Nicht erreicht" zu={() => setFrage(null)} laeuft={laeuft}
                   ok={() => tu(() => api.ne4Kontakt(id, { ergebnis: 'nicht_erreicht', text, ...(gewaehlt ?? {}), ...(wann ? { wann: new Date(wann).toISOString() } : {}) }),
                                wann ? 'Nicht erreicht, Wiedervorlage gesetzt.' : 'Nicht erreicht, nächster Versuch morgen zur anderen Tageszeit.')}
                   okText="Vermerken">
          {/* WELCHE NUMMER (.346). Marvin: „dass du auswählst, welche Rufnummer
              du anrufst ... eine Stunde später rufst du wieder die erste
              Nummer an." Vorbelegt ist die zuletzt gewählte; jede Wahl ist
              ein eigener Vermerk, auch dieselbe Nummer noch einmal. */}
          <div className="klein" style={{ marginBottom: 8 }}>
            <div style={{ marginBottom: 4 }}>Bei welcher Nummer?</div>
            <div className="reihe" style={{ flexWrap: 'wrap', gap: 10 }}>
              {([['mobil', a.mobil], ['telefon', a.telefon]] as const).filter(([, n]) => !!n).map(([w, n]) => (
                <label key={w} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" name="nummer" checked={gewaehlt?.welche === w} onChange={() => setGewaehlt({ welche: w, nummer: n! })} />
                  {w === 'mobil' ? 'Mobil' : 'Festnetz'} {n}
                </label>
              ))}
              {!a.mobil && !a.telefon && <span className="leise">keine Nummer hinterlegt</span>}
            </div>
          </div>
          <div className="feld"><label>Wiedervorlage (leer: morgen zur anderen Tageszeit)</label><input type="datetime-local" value={wann} onChange={(e) => setWann(e.target.value)} /></div>
          <Text name="text" titel="Notiz" wert={text} setze={setText} fehler={fehler} />
        </Nachfrage>
      )}
      {frage === 'rueckruf' && (
        <Nachfrage titel="Rückruf gewünscht" zu={() => setFrage(null)} laeuft={laeuft} ok={() => tu(() => api.ne4Kontakt(id, { ergebnis: 'rueckruf', wann: new Date(wann).toISOString(), text }), 'Wiedervorlage gesetzt.')} okText="Wiedervorlage setzen" gesperrt={!wann}>
          <div className="feld"><label>Wann</label><input type="datetime-local" value={wann} onChange={(e) => setWann(e.target.value)} /></div>
          <Text name="text" titel="Notiz" wert={text} setze={setText} fehler={fehler} />
        </Nachfrage>
      )}
      {frage === 'mail' && a.art === 'GEB' && (
        <MailVorschau auftragId={id} schluessel="haus_nachfassen" titel="Nachfass-Mail an den Eigentümer" email={a.email}
                      hinweis="Wenn wir den Eigentümer nicht erreichen. Danach liegt das Haus nach den Nachfass-Tagen wieder auf der Anrufliste."
                      zu={() => setFrage(null)}
                      senden={(w) => tu(() => api.ne4Kontakt(id, { ergebnis: 'nachfass_mail', email: w.email, betreff: w.betreff, text: w.text }), 'Nachfass-Mail gesendet, Wiedervorlage gesetzt.')} />
      )}
      {frage === 'mail' && a.art !== 'GEB' && (
        <Nachfrage titel="Mail erneut senden" zu={() => setFrage(null)} laeuft={laeuft}
                   ok={() => tu(() => api.ne4Kontakt(id, { ergebnis: 'mail_erneut', email: mail }), 'Mail gesendet.')} okText="Senden" gesperrt={!mail.trim()}>
          <Text name="email" titel="An diese Adresse" art="email" wert={mail} setze={setMail} fehler={fehler} hinweis="Eine andere Adresse wird am Auftrag gespeichert." />
        </Nachfrage>
      )}
      {frage === 'ablehnen' && (
        <Nachfrage titel="Kunde lehnt ab" zu={() => setFrage(null)} laeuft={laeuft} ok={() => tu(() => api.ne4Kontakt(id, { ergebnis: 'kunde_lehnt_ab', text }), 'Zurück an die Telekom, mit Grund.')} okText="Zurückgeben" gesperrt={!text.trim()}>
          <Text name="text" titel="Grund" wert={text} setze={setText} pflicht fehler={fehler} mehrzeilig />
        </Nachfrage>
      )}
      {frage === 'rueckgabe' && (
        <Nachfrage titel="Rückgabe an die Telekom" zu={() => setFrage(null)} laeuft={laeuft} ok={() => tu(() => api.ne4Rueckgabe(id, text || 'Kunde nicht erreicht'), 'Zurückgegeben.')} okText="Bestätigen">
          <Text name="text" titel="Wartegrund" wert={text} setze={setText} fehler={fehler} platzhalter="Kunde nicht erreicht" />
        </Nachfrage>
      )}
      {frage === 'absagen' && a.termin && (
        <Nachfrage titel="Termin absagen" zu={() => setFrage(null)} laeuft={laeuft} ok={() => tu(() => api.ne4TerminAbsagen(a.termin.id, text || 'abgesagt'), 'Abgesagt, zurück in die Anrufliste.')} okText="Absagen">
          <Text name="text" titel="Grund" wert={text} setze={setText} fehler={fehler} />
        </Nachfrage>
      )}
      {frage === 'block_absagen' && (
        <Nachfrage titel="Block absagen" zu={() => setFrage(null)} laeuft={laeuft} ok={() => tu(() => api.ne4BlockAbsagen(id, text || 'abgesagt'), 'Block abgesagt, Haus zurück in die Anrufliste.')} okText="Absagen">
          <Text name="text" titel="Grund" wert={text} setze={setText} fehler={fehler} />
        </Nachfrage>
      )}
      {frage === 'aushang' && (
        <MailVorschau auftragId={id} schluessel="haus_aushang" titel="Aushang an den Eigentümer" email={a.email} zu={() => setFrage(null)}
                      senden={(w) => tu(() => api.ne4HausMail(id, 'haus_aushang', w.email || undefined, { betreff: w.betreff, text: w.text }), 'Aushang gesendet.')} />
      )}
      {frage === 'bestaetigung' && (
        /* .587: IN EINEM ZUG. Marvin, 02.11.2026: „wenn du einen Terminvorschlag gemacht hast, dann ... sagt er dir
           dann auch, jetzt Terminbestätigung per Mail schicken, direkt in einem Zug ... sonst vergisst du das
           nachher". Nach dem Setzen öffnet sich dieses Fenster von selbst; gesendet steht es in der Auftragsinfo,
           nicht gesendet bleibt am Termin der Hinweis mit „jetzt senden". */
        <MailVorschau auftragId={id} schluessel="haus_bestaetigung" titel="Termin gesetzt: jetzt die Terminbestätigung senden" email={a.email} zu={() => setFrage(null)}
                      hinweis="Der Termin steht. Mail und Aushang kurz prüfen, dann „Terminbestätigung senden“. Gesendet steht sie mit Anhang in der Auftragsinfo."
                      spaeterText="Später senden" sendenText="Terminbestätigung senden"
                      senden={(w) => tu(() => api.ne4HausMail(id, 'haus_bestaetigung', w.email || undefined, { betreff: w.betreff, text: w.text }), 'Terminbestätigung mit Aushang gesendet, steht in der Auftragsinfo.')} />
      )}
      {frage === 'erinnerung' && (
        <MailVorschau auftragId={id} schluessel="haus_erinnerung" titel="Erinnerung an den Eigentümer" email={a.email} zu={() => setFrage(null)}
                      hinweis="Geht von selbst vor dem ersten Tag (Tage vorher laut Konfiguration und am Vortag)." />
      )}
      {frage === 'status' && <StatusDialog a={a} zu={() => setFrage(null)} fertig={(k) => { setA(k); setFrage(null); setMeldung('Status gespeichert, steht im Verlauf.'); geaendert(); }} />}
      {frage === 'telekom' && <TelekomMeldung a={a} zu={() => setFrage(null)} fertig={(k, t) => { setA(k); setFrage(null); setMeldung(t); geaendert(); }} />}
      {frage === 'notiz' && (
        <Nachfrage titel="Notiz" zu={() => setFrage(null)} laeuft={laeuft} ok={() => tu(() => api.ne4Kontakt(id, { ergebnis: 'notiz', text }), 'Notiert.')} okText="Speichern" gesperrt={!text.trim()}>
          <Text name="text" titel="Text" wert={text} setze={setText} pflicht fehler={fehler} mehrzeilig />
        </Nachfrage>
      )}
      {frage === 'bearbeiten' && <BearbeitenDialog a={a} zu={() => setFrage(null)} fertig={(k) => { setA(k); setFrage(null); geaendert(); }} />}
      {frage === 'kalender' && <PlatzWahl id={id} zu={() => setFrage(null)} waehle={(p) => { setFrage(null); buchen(p); }} />}
    </>
  );
  if (seite) {
    return (
      <>
        {/* .557: DER LINK WIE IN NE3. Marvin: „oben rechts die KLS, die wird anders verlinkt wie in NE3. Das baust du bitte um."
            Kopiert wird „MDS Link: …/#/a/ne4/<KLS-ID>"; der Link öffnet das Haus im Bereich NE4. */}
        {/* .586: DER KOPF WIE IN NE3. Marvin, 02.11.2026: „bitte bei ne4, wie bei NE3, die
            Zurückpfeile oder sowas, all das, was es gibt, und auch Link öffnen, alles auf die
            linke Seite mit rüberpacken, so wie es bei NE3 auch ist, damit es identisch ist ...
            die Leute, auch in der Dispo, die arbeiten da gleich". Reihenfolge wie in der Akte:
            zurück, Anschrift, Zustand, Link kopieren; rechts nur die Route. */}
        <div className="kopf">
          <button type="button" className="knopf" onClick={zu}>&larr; zurück</button>
          <h1>{titel}</h1>
          <span className="weg">{untertitel}, KLS-ID <KlsKopie kls={a.kls_id} /></span>
          {/* .620: Status von Hand. Marvin, 03.11.2026: „ich will den Status ändern können." */}
          {kannPflegen
            ? <button type="button" className="alslink zustandwahl" title="Status ändern" onClick={() => { setText(''); setFrage('status'); }}><Zustandschip z={a.zustand} inArbeit={a.in_arbeit_seit} /> <span className="klein leise">ändern</span></button>
            : <Zustandschip z={a.zustand} />}
          {a.art === 'GEB' && <BulkZeile b={a.bulk_kurz} kurz />}
          <button type="button" className="knopf klein" title={quicklink(a, undefined, 'ne4')}
                  onClick={async () => { const t = `MDS Link: ${quicklink(a, undefined, 'ne4')}`; try { await navigator.clipboard.writeText(t); setLinkKopiert('kopiert'); } catch { setLinkKopiert(t); } setTimeout(() => setLinkKopiert(null), 4000); }}>
            Link kopieren
          </button>
          {/* .593: DEN AUFTRAG PER MAIL WEITERGEBEN (wie in NE3): Anschrift und KLS-ID im Betreff. */}
          <button type="button" className="knopf klein" title="Empfänger aus dem Adressbuch wählen, dann öffnet sich das Mailprogramm mit dem Auftrag im Betreff" onClick={() => setMailOffen(true)}>E-Mail</button>
          {mailOffen && <MailMitAdressbuch bereich="ne4" betreff={auftragsBetreff(a)} zu={() => setMailOffen(false)} />}
          {/* .620: Auftrag an das NE4-Postfach der Telekom melden (Vorlage, Kundenmails als Anhang, danach zurückgegeben). */}
          {kannPflegen && a.art === 'GEB' && <button type="button" className="knopf klein" style={{ whiteSpace: 'nowrap' }} onClick={() => setFrage('telekom')}>An Telekom melden</button>}
          {linkKopiert && <span className="klein leise" style={{ wordBreak: 'break-all' }}>{linkKopiert}</span>}
          <div className="wachsen" />
        </div>
        {a.art === 'GEB' && (
          <div className="reiter">
            {([['uebersicht', 'Übersicht'], ['connects', `Connects (${(a.wohnungen ?? []).length})`], ['dateien', 'Dateien']] as [HausReiter, string][]).map(([k, n]) => (
              <button key={k} type="button" aria-current={reiter === k} onClick={() => setReiter(k)}>{n}</button>
            ))}
          </div>
        )}
        <div className="inhalt">{inhalt}</div>
      </>
    );
  }
  return <Dialog titel={titel} untertitel={untertitel} breite={a.art === 'GEB' ? 1500 : 900} laeuft={laeuft} zu={zu}>{inhalt}</Dialog>;
}

function Nachfrage({ titel, children, zu, ok, okText, laeuft, gesperrt }: { titel: string; children: any; zu: () => void; ok: () => void; okText: string; laeuft: boolean; gesperrt?: boolean }) {
  return (
    <Dialog titel={titel} breite={520} laeuft={laeuft} zu={zu}>
      {children}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        <button type="button" className="knopf haupt" disabled={laeuft || !!gesperrt} onClick={ok}>{okText}</button>
      </div>
    </Dialog>
  );
}

/* .495: Etagen und Wohneinheiten eines Hauses, mit der Kalkulation daneben. */
function HausFelder({ w, setze, fehler }: { w: any; setze: (k: string) => (v: string) => void; fehler: Fehlerlage | null }) {
  const [k, setK] = useState<any>(null);
  useEffect(() => {
    if (!(Number(w.wohneinheiten) > 0)) { setK(null); return; }
    const t = setTimeout(() => { api.ne4Kalkulation(w.etagen || 0, w.wohneinheiten).then(setK).catch(() => setK(null)); }, 300);
    return () => clearTimeout(t);
  }, [w.etagen, w.wohneinheiten]);
  return (
    <div className="reihe" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
      <Text name="etagen" titel="Etagen" wert={w.etagen} setze={setze('etagen')} fehler={fehler} breite={100} />
      <Text name="wohneinheiten" titel="Wohneinheiten" wert={w.wohneinheiten} setze={setze('wohneinheiten')} fehler={fehler} breite={120} pflicht />
      {k && <div className="klein leise" style={{ paddingBottom: 10 }}>kalkuliert {Math.round(k.minuten / 6) / 10} h, {k.tage} Tag{k.tage === 1 ? '' : 'e'}{k.ist?.haeuser ? ` (Ist aus ${k.ist.haeuser} Häusern: ${k.ist.min_je_we} min je WE)` : ''}</div>}
    </div>
  );
}

function BearbeitenDialog({ a, zu, fertig }: { a: any; zu: () => void; fertig: (k: any) => void }) {
  const [w, setW] = useState<any>({ name: a.name ?? '', vorname: a.vorname ?? '', telefon: a.telefon ?? '', mobil: a.mobil ?? '', email: a.email ?? '', notiz: a.notiz ?? '', art: a.art, frist: a.frist ? String(a.frist).slice(0, 10) : '', etagen: a.etagen ? String(a.etagen) : '', wohneinheiten: a.wohneinheiten ? String(a.wohneinheiten) : '' });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const setze = (k: string) => (v: string) => setW({ ...w, [k]: v });
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { fertig(await api.ne4AuftragAendern(a.id, { ...w, frist: w.frist || null })); } catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel="Kundendaten ändern" untertitel={`KLS-ID ${a.kls_id}`} breite={600} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <div className="reihe"><Text name="vorname" titel="Vorname" wert={w.vorname} setze={setze('vorname')} fehler={fehler} breite={200} /><Text name="name" titel="Name" wert={w.name} setze={setze('name')} fehler={fehler} breite={260} /></div>
      <div className="reihe"><Text name="telefon" titel="Telefon" art="tel" wert={w.telefon} setze={setze('telefon')} fehler={fehler} breite={200} /><Text name="mobil" titel="Mobil" art="tel" wert={w.mobil} setze={setze('mobil')} fehler={fehler} breite={200} /></div>
      <Text name="email" titel="E-Mail" art="email" wert={w.email} setze={setze('email')} fehler={fehler} />
      <div className="reihe">
        <Auswahl name="art" titel="Art" wert={w.art} setze={setze('art')} fehler={fehler} optionen={[{ wert: 'HC', text: 'HC (Home Connect)' }, { wert: 'GWV', text: 'GWV (Gewerksvorbereitung)' }, { wert: 'GEB', text: 'Gebäude (Sammelpunkt, Stern, Dose je WE)' }]} breite={260} />
        <Text name="frist" titel="Frist der Telekom" art="date" wert={w.frist} setze={setze('frist')} fehler={fehler} breite={180} />
      </div>
      {w.art === 'GEB' && <HausFelder w={w} setze={setze} fehler={fehler} />}
      <Text name="notiz" titel="Notiz" wert={w.notiz} setze={setze('notiz')} fehler={fehler} mehrzeilig />
      <div className="reihe" style={{ marginTop: 12 }}><button type="button" className="knopf" onClick={zu}>Abbrechen</button><button type="button" className="knopf haupt" disabled={laeuft} onClick={speichern}>Speichern</button></div>
    </Dialog>
  );
}

/**
 * STATUS VON HAND (.620).
 *
 * ORIGINALTEXT MARVIN (03.11.2026)
 *   „ich will den Status ändern können." Auf Nachfrage: „ja alle status sollen wählbar sein".
 *
 * Alle sieben Werte, der jetzige vorgewählt. „In Arbeit" ist kein eigener Wert (das ergibt sich aus dem Termin).
 * Grund frei, keine Pflicht (im Entwurf gefragt, keine Pflicht verlangt). Gespeichert steht es im Verlauf.
 */
function StatusDialog({ a, zu, fertig }: { a: any; zu: () => void; fertig: (k: any) => void }) {
  const [wahl, setWahl] = useState<string>(a.zustand);
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { fertig(await api.ne4Zustand(a.id, wahl, grund.trim() || null)); } catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel="Status ändern" untertitel={[a.strasse, a.hausnummer].filter(Boolean).join(' ') + (a.ort ? `, ${[a.plz, a.ort].filter(Boolean).join(' ')}` : '')} breite={480} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <div className="klein leise" style={{ marginBottom: 10 }}>Jetzt: <b>{ZUSTAND[a.zustand] ?? a.zustand}</b>. „In Arbeit“ ist kein eigener Status, das ergibt sich aus dem Termin.</div>
      <div className="statuswahl" role="radiogroup" aria-label="Status">
        {Object.keys(ZUSTAND).map((k) => (
          <label key={k} className={'statuszeile' + (wahl === k ? ' gewaehlt' : '')}>
            <input type="radio" name="ne4_status" checked={wahl === k} onChange={() => setWahl(k)} />
            <Zustandschip z={k} />
            {k === a.zustand && <span className="klein leise" style={{ marginLeft: 'auto' }}>jetzt</span>}
          </label>
        ))}
      </div>
      <Text name="grund" titel="Grund (frei, wird im Verlauf vermerkt)" wert={grund} setze={setGrund} fehler={fehler} mehrzeilig zeilen={3} />
      <div className="reihe" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
        <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        <button type="button" className="knopf haupt" disabled={laeuft || wahl === a.zustand} onClick={speichern}>Status speichern</button>
      </div>
    </Dialog>
  );
}

/**
 * EINEN AUFTRAG VON HAND ANLEGEN (.338). Marvin: „Oder wie lege ich die an in
 * dem Bereich manuell?" Dieselben Felder wie aus der Excel; danach geht die
 * Auftragskarte auf.
 */
function NeuDialog({ zu, fertig }: { zu: () => void; fertig: (k: any) => void }) {
  const [w, setW] = useState<any>({ kls_id: '', art: 'HC', vorname: '', name: '', strasse: '', hausnummer: '', plz: '', ort: '', telefon: '', mobil: '', email: '', frist: '', notiz: '', etagen: '', wohneinheiten: '' });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const setze = (k: string) => (v: string) => setW({ ...w, [k]: v });
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { fertig(await api.ne4AuftragAnlegen({ ...w, frist: w.frist || null })); } catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel="Auftrag von Hand anlegen" untertitel="Wie aus der Excel der Telekom; der Punkt wird sofort gesucht, das Gebiet zugeordnet." breite={640} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <div className="reihe">
        <Text name="kls_id" titel="KLS-ID" pflicht wert={w.kls_id} setze={setze('kls_id')} fehler={fehler} breite={200} />
        <Auswahl name="art" titel="Art" wert={w.art} setze={setze('art')} fehler={fehler} optionen={[{ wert: 'HC', text: 'HC (Home Connect)' }, { wert: 'GWV', text: 'GWV (Gewerksvorbereitung)' }, { wert: 'GEB', text: 'Gebäude (Sammelpunkt, Stern, Dose je WE)' }]} breite={260} />
        <Text name="frist" titel="Frist der Telekom" art="date" wert={w.frist} setze={setze('frist')} fehler={fehler} breite={170} />
      </div>
      {w.art === 'GEB' && <HausFelder w={w} setze={setze} fehler={fehler} />}
      <div className="reihe"><Text name="vorname" titel={w.art === 'GEB' ? 'Vorname (Eigentümer)' : 'Vorname'} wert={w.vorname} setze={setze('vorname')} fehler={fehler} breite={200} /><Text name="name" titel={w.art === 'GEB' ? 'Name (Eigentümer / Hausverwaltung)' : 'Name'} wert={w.name} setze={setze('name')} fehler={fehler} breite={260} /></div>
      <div className="reihe"><Text name="strasse" titel="Straße" pflicht wert={w.strasse} setze={setze('strasse')} fehler={fehler} breite={300} /><Text name="hausnummer" titel="Nr." wert={w.hausnummer} setze={setze('hausnummer')} fehler={fehler} breite={90} /></div>
      <div className="reihe"><Text name="plz" titel="PLZ" pflicht wert={w.plz} setze={setze('plz')} fehler={fehler} breite={110} /><Text name="ort" titel="Ort" wert={w.ort} setze={setze('ort')} fehler={fehler} breite={280} /></div>
      <div className="reihe"><Text name="telefon" titel="Telefon" art="tel" wert={w.telefon} setze={setze('telefon')} fehler={fehler} breite={200} /><Text name="mobil" titel="Mobil" art="tel" wert={w.mobil} setze={setze('mobil')} fehler={fehler} breite={200} /></div>
      <Text name="email" titel="E-Mail" art="email" wert={w.email} setze={setze('email')} fehler={fehler} />
      <Text name="notiz" titel="Notiz" wert={w.notiz} setze={setze('notiz')} fehler={fehler} mehrzeilig />
      <div className="reihe" style={{ marginTop: 12 }}><button type="button" className="knopf" onClick={zu}>Abbrechen</button><button type="button" className="knopf haupt" disabled={laeuft} onClick={speichern}>Anlegen</button></div>
    </Dialog>
  );
}

/** Alle freien Plätze im Fenster, nach Tag, zum Wählen. */
function PlatzWahl({ id, zu, waehle }: { id: string; zu: () => void; waehle: (p: any) => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  useEffect(() => { api.ne4Plaetze(id).then(setD).catch((f) => setFehler(ausFehler(f))); }, [id]);
  const tage = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const p of d?.plaetze ?? []) { if (!m.has(p.tag)) m.set(p.tag, []); m.get(p.tag)!.push(p); }
    return [...m.entries()];
  }, [d]);
  return (
    <Dialog titel="Freien Platz wählen" breite={760} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {d && !tage.length && <div className="leise klein">In den nächsten 2 Wochen ist kein Platz frei. Unter Gebiete und Teamtage einen Teamtag anlegen.</div>}
      {tage.map(([tag, liste]) => (
        <div key={tag} style={{ marginBottom: 10 }}>
          <b>{tagLang(tag)}</b>
          <div className="reihe" style={{ flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {liste.map((p: any) => (
              <button key={p.teamtag_id + p.beginn} type="button" className="knopf klein" title={`${p.team} (${p.firma}), ${p.gebiet ?? ''}`} onClick={() => waehle(p)}>
                {uhr(p.beginn)} Uhr, {p.team}{p.gebiet ? `, ${p.gebiet}` : ''}{p.frei_geworden ? ', frei geworden' : ''}
              </button>
            ))}
          </div>
        </div>
      ))}
    </Dialog>
  );
}

/* ================================================================ Anrufliste */

export function Ne4Anrufliste({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [offen, setOffen] = useState<{ id: string; start?: Frage } | null>(() => { const x = offenAusAdresse('ne4_anrufliste'); return x ? { id: x } : null; });
  useEffect(() => { offenInAdresse('ne4_anrufliste', offen?.id ?? null); }, [offen?.id]);
  const [nurFaellig, setNurFaellig] = useState(true);
  /* FILTER IN DER TITELLEISTE (.515). Marvin, 28.10.2026: „Nach was sind die
     sortiert? Ich muss die irgendwie oben über die Titelleiste filtern
     können, damit ich weiß, was Sache ist." Suche, Ort, Eigentümer oder
     Verwaltung, Stand im Bulk-Portal. */
  const [suche, setSuche] = useState('');
  const [ort, setOrt] = useState('');
  const [wer, setWer] = useState('');
  const [bulk, setBulk] = useState('');
  /* WELCHE NUMMER ZULETZT AUS DER ZEILE GEWÄHLT WURDE (.364): das Ergebnis
     „nicht erreicht" nennt sie dann im Protokoll, ohne dass die Karte auf
     war. ORIGINALTEXT MARVIN (14.10.2026): „Anrufliste, wenn ich ein
     Telefongespräch geführt habe ... Was habe ich danach für eine Auswahl ...
     Was ist, wenn dort zwei Rufnummern stehen? ... als nächstes anrufen, wie
     selektiere ich da was?" */
  const [gewaehlt, setGewaehlt] = useState<Record<string, { welche: 'mobil' | 'telefon'; nummer: string }>>({});
  const [vermerkt, setVermerkt] = useState<Record<string, string>>({});
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const laden = () => api.ne4Anrufliste().then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const alleZeilen: any[] = d?.zeilen ?? [];
  const werVon = (a: any) => a.eigentuemer_org || [a.vorname, a.name].filter(Boolean).join(' ') || '';
  const orte = [...new Set<string>(alleZeilen.map((a) => String(a.ort ?? '')).filter(Boolean))].sort((x, y) => x.localeCompare(y, 'de'));
  const wers = [...new Set<string>(alleZeilen.map(werVon).filter(Boolean))].sort((x, y) => x.localeCompare(y, 'de'));
  const such = suche.trim().toLowerCase();
  const zeilen: any[] = alleZeilen.filter((z: any) => (!nurFaellig || z.faellig || z.rueckgabe_vorgemerkt)
    && (!ort || z.ort === ort)
    && (!wer || werVon(z) === wer)
    && (!bulk || (bulk === 'wartegrund' ? !!z.bulk_kurz?.wartegrund : bulk === 'auskundung_neu' ? !!z.bulk_kurz?.auskundung_neu : z.bulk_kurz?.zustand === bulk))
    /* .590: wie in NE3: jedes Wort muss passen, „Straße" gleich „Str.". */
    && (!such || such.replace(/[,;]/g, ' ').split(/\s+/).filter(Boolean).every((w) => passtSuche(w, z.kls_id, z.name, z.vorname, z.eigentuemer_org, `${z.strasse ?? ''} ${z.hausnummer ?? ''}`, z.plz, z.ort, z.email, z.mobil, z.telefon))));
  const naechste = useRef<HTMLButtonElement>(null);
  const erste = zeilen[0];
  /* MOBIL VOR FESTNETZ: wer unterwegs ist, hebt am Handy ab. */
  const ersteWahl = erste ? (erste.mobil ? { welche: 'mobil' as const, nummer: erste.mobil } : erste.telefon ? { welche: 'telefon' as const, nummer: erste.telefon } : null) : null;
  const andereWahl = erste && ersteWahl?.welche === 'mobil' && erste.telefon ? { welche: 'telefon' as const, nummer: erste.telefon } : null;
  const name = (a: any) => [a.vorname, a.name].filter(Boolean).join(' ') || a.eigentuemer_org || a.kls_id;

  /* Der große Knopf wählt UND öffnet die Karte (da stehen die Vorschläge);
     die Knöpfe in der Zeile wählen nur, das Ergebnis kommt dann aus der Zeile. */
  const anrufen = (a: any, w: { welche: 'mobil' | 'telefon'; nummer: string }, oeffnen = false) => {
    setGewaehlt((g) => ({ ...g, [a.id]: w }));
    waehlen(a.id, w.welche, w.nummer).then(() => { laden(); if (oeffnen) setOffen({ id: a.id }); });
  };
  /* ERGEBNIS AUS DER ZEILE, OHNE DIE KARTE: „Nicht erreicht" und „Falsche
     Nummer" brauchen keine weitere Angabe. Rückruf (wann?) und Ablehnung
     (warum?) öffnen die Karte gleich an der richtigen Frage. Die Zeile
     bleibt kurz mit dem Vermerk stehen und rückt dann weiter. */
  const ergebnis = async (a: any, was: 'nicht_erreicht' | 'falsche_nummer') => {
    setLaeuft(a.id); setFehler(null);
    try {
      await api.ne4Kontakt(a.id, { ergebnis: was, ...(was === 'nicht_erreicht' ? (gewaehlt[a.id] ?? {}) : {}) });
      setVermerkt((v) => ({ ...v, [a.id]: was === 'nicht_erreicht' ? 'Nicht erreicht vermerkt, morgen wieder dran.' : 'Falsche Nummer vermerkt.' }));
      setTimeout(() => { setVermerkt((v) => { const n = { ...v }; delete n[a.id]; return n; }); laden(); }, 1800);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(null); }
  };
  /* GEBÜNDELT: die Häuser derselben Verwaltung stehen hintereinander; die
     erste Zeile eines Bündels trägt den Namen, die weiteren rücken ein. */
  const gleicheWie = (i: number) => i > 0 && !!werVon(zeilen[i]) && werVon(zeilen[i]) === werVon(zeilen[i - 1]);
  const anzahlVon = (a: any) => zeilen.filter((z) => werVon(z) && werVon(z) === werVon(a)).length;
  /* DIE AKTE ALS SEITE (.520), aus der Anrufliste heraus mit Wechsel zwischen den Häusern der Verwaltung. */
  if (offen) return <AuftragsKarte ich={ich} id={offen.id} start={offen.start} seite gewaehltVorher={gewaehlt[offen.id] ?? null} zu={() => { setOffen(null); laden(); }} geaendert={laden} wechsel={(id) => setOffen({ id })} />;

  return (
    <>
      <Kopf titel="NE4, Anrufliste" weg={d ? `${d.zahlen.faellig} fällig, ${d.zahlen.wiedervorlagen} Wiedervorlagen, ${d.zahlen.rueckgabe} zur Rückgabe, ${d.zahlen.alle} offen` : ''}
            rechts={<div className="reihe" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <Suchfeld wert={suche} setze={setSuche} mehr="Name, Eigentümer, Nummer" breit={260} />
              <select value={ort} onChange={(e) => setOrt(e.target.value)} title="Ort"><option value="">alle Orte</option>{orte.map((o) => <option key={o} value={o}>{o}</option>)}</select>
              <select value={wer} onChange={(e) => setWer(e.target.value)} title="Eigentümer oder Verwaltung" style={{ maxWidth: 260 }}><option value="">alle Eigentümer</option>{wers.map((o) => <option key={o} value={o}>{o}</option>)}</select>
              <select value={bulk} onChange={(e) => setBulk(e.target.value)} title="Stand im Bulk-Portal">
                <option value="">GBGS: alle</option><option value="waehlbar">NE4 wählbar</option><option value="wartet">NE4 warten</option><option value="wartegrund">mit Wartegrund</option><option value="auskundung_neu">neue Auskundung</option><option value="weg">aus dem Bulk verschwunden</option>
              </select>
              <label className="klein" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={nurFaellig} onChange={(e) => setNurFaellig(e.target.checked)} /> nur fällige</label>
              {(suche || ort || wer || bulk) && <button type="button" className="knopf klein" onClick={() => { setSuche(''); setOrt(''); setWer(''); setBulk(''); }}>Zurücksetzen</button>}
            </div>} />
      <div className="inhalt">
      <Fehlerkasten lage={fehler} />
      <Karte titel={`Anrufen (${zeilen.length}${zeilen.length !== alleZeilen.length ? ` von ${alleZeilen.length}` : ''})`}>
        <p className="klein leise" style={{ marginTop: 0 }}><b>Reihenfolge:</b> zuerst Rückgaben und fällige Wiedervorlagen, dann was heute dran ist; darin die Häuser derselben Hausverwaltung hintereinander (ein Anruf für alle), nach Ort und Straße. Der große Knopf wählt und öffnet die Karte, das Ergebnis geht auch direkt aus der Zeile.</p>
        {d && !zeilen.length && <div className="leise klein">Nichts fällig{(ort || wer || bulk || such) ? ' mit diesem Filter' : ''}. {d.zahlen.alle ? `${d.zahlen.alle} Aufträge warten noch auf die Mail oder den nächsten Versuch.` : ''}</div>}
        {!!zeilen.length && (
          <>
            <div className="reihe" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {ersteWahl
                ? <button ref={naechste} type="button" className="knopf haupt" onClick={() => anrufen(erste, ersteWahl, true)} title="Vermerkt die Wahl im Auftrag, wählt und öffnet die Karte">
                    📞 Nächsten anrufen: {name(erste)}, {ersteWahl.welche === 'mobil' ? 'Mobil' : 'Festnetz'} {ersteWahl.nummer}{anzahlVon(erste) > 1 ? `, ${anzahlVon(erste)} Häuser` : ''}
                  </button>
                : <button ref={naechste} type="button" className="knopf haupt" onClick={() => setOffen({ id: erste.id })}>Nächsten öffnen: {name(erste)} <span className="leise">(keine Nummer)</span></button>}
              {andereWahl && <button type="button" className="knopf" onClick={() => anrufen(erste, andereWahl, true)}>stattdessen Festnetz {andereWahl.nummer}</button>}
            </div>
            <table className="liste ne4-anrufliste">
              {/* .563: GBGS SCHMALER UND MEHRZEILIG, „Bisher" BREITER. Marvin: „GBGS die Spalte kleiner reduzieren, mehrspaltig, dafür dann hinten bisher größer, dass das nicht so gequetscht ist." */}
              {/* .595: neun Spalten seit „Einheiten" (.593); die Breiten stimmten nicht mehr, die Tabelle lief rechts aus dem Bild. */}
              <colgroup><col style={{ width: '15%' }} /><col style={{ width: '7%' }} /><col style={{ width: '11%' }} /><col style={{ width: '5%' }} /><col style={{ width: '14%' }} /><col style={{ width: '11%' }} /><col style={{ width: '13%' }} /><col style={{ width: '14%' }} /><col style={{ width: '10%' }} /></colgroup>
              <thead><tr><th>Eigentümer / Kunde</th><th>KLS-ID</th><th>Anschrift</th><th>Einheiten</th><th>GBGS</th><th>Warum jetzt</th><th>Anrufen</th><th>Ergebnis</th><th>Bisher</th></tr></thead>
              <tbody>
                {zeilen.map((a, i) => (
                  <tr key={a.id} onClick={() => setOffen({ id: a.id })} className={a.id === erste.id ? 'naechste' : ''}>
                    <td>
                      {gleicheWie(i)
                        ? <span className="klein leise" style={{ paddingLeft: 14 }}>↳ dieselbe Verwaltung</span>
                        : <>
                            <b>{a.eigentuemer_org || [a.vorname, a.name].filter(Boolean).join(' ') || <span className="leise">ohne Namen</span>}</b>
                            {a.eigentuemer_org && [a.vorname, a.name].filter(Boolean).length > 0 && <div className="klein">{[a.vorname, a.name].filter(Boolean).join(' ')}</div>}
                            {anzahlVon(a) > 1 && <div className="klein leise">{anzahlVon(a)} Häuser in der Liste</div>}
                          </>}
                    </td>
                    <td className="klein"><KlsKopie kls={a.kls_id} />{a.art !== 'GEB' && <div className="leise">{a.art === 'HC' ? 'Home Connect' : a.art === 'GWV' ? 'Gewerksvorbereitung' : a.art}</div>}</td>
                    <td className="klein">{a.strasse} {a.hausnummer}<div className="leise">{a.plz} {a.ort}</div></td>
                    <td className="klein" style={{ whiteSpace: 'nowrap' }}>{a.wohneinheiten ?? '–'} WE<br />{a.ge ?? '–'} GE<br />{a.sl ?? '–'} SL</td>
                    <td className="klein ne4-gbgs-spalte"><BulkZeile b={a.bulk_kurz} /></td>
                    <td className="klein">
                      {a.rueckgabe_vorgemerkt ? <span style={{ color: 'var(--rot, #a33)' }}>Rückgabe bestätigen</span>
                        : a.wiedervorlage && new Date(a.wiedervorlage).getTime() <= Date.now() ? <>Wiedervorlage {zeitpunkt(a.wiedervorlage)}</>
                        : a.faellig ? (a.zustand === 'import' ? 'neu, noch nie angerufen' : a.naechster_teamtag ? 'Teamtag im Gebiet, Platz füllen' : 'fällig') : <span className="leise">ab {zeitpunkt(a.naechster_anruf)}</span>}
                    </td>
                    <td className="klein" onClick={(e) => e.stopPropagation()}>
                      <div className="reihe" style={{ flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {a.mobil && <button type="button" className={`knopf klein${gewaehlt[a.id]?.welche === 'mobil' ? ' haupt' : ''}`} onClick={() => anrufen(a, { welche: 'mobil', nummer: a.mobil })} title="Vermerkt die Wahl im Auftrag und wählt">📞 Mobil {a.mobil}</button>}
                        {a.telefon && <button type="button" className={`knopf klein${gewaehlt[a.id]?.welche === 'telefon' ? ' haupt' : ''}`} onClick={() => anrufen(a, { welche: 'telefon', nummer: a.telefon })} title="Vermerkt die Wahl im Auftrag und wählt">📞 Festnetz {a.telefon}</button>}
                        {!a.mobil && !a.telefon && <span className="leise">keine Nummer</span>}
                      </div>
                    </td>
                    <td className="klein" onClick={(e) => e.stopPropagation()}>
                      {vermerkt[a.id]
                        ? <span className="hinweis erledigt" style={{ padding: '2px 8px', display: 'inline-block' }}>{vermerkt[a.id]}</span>
                        : a.rueckgabe_vorgemerkt
                          ? <button type="button" className="knopf klein" onClick={() => setOffen({ id: a.id, start: 'rueckgabe' })}>Rückgabe bestätigen</button>
                          : (
                            <div className="reihe" style={{ flexWrap: 'wrap', gap: 4 }}>
                              <button type="button" className="knopf klein" disabled={laeuft === a.id} onClick={() => ergebnis(a, 'nicht_erreicht')} title={gewaehlt[a.id] ? `Vermerkt ${gewaehlt[a.id].welche === 'mobil' ? 'Mobil' : 'Festnetz'} ${gewaehlt[a.id].nummer} als nicht erreicht` : 'Vermerkt nicht erreicht, morgen zur anderen Tageszeit wieder dran'}>Nicht erreicht</button>
                              <button type="button" className="knopf klein" onClick={() => setOffen({ id: a.id, start: 'rueckruf' })} title="Fragt nach dem Zeitpunkt">Rückruf</button>
                              <button type="button" className="knopf klein" onClick={() => setOffen({ id: a.id, start: 'ablehnen' })} title="Fragt nach dem Grund">Lehnt ab</button>
                              <button type="button" className="knopf klein" disabled={laeuft === a.id} onClick={() => ergebnis(a, 'falsche_nummer')}>Falsche Nummer</button>
                            </div>
                          )}
                    </td>
                    <td className="klein leise">{a.anrufe} Anrufe, {a.mails} Mails<div>{a.letzter_kontakt ? `zuletzt ${zeitpunkt(a.letzter_kontakt)}` : 'noch nie angerufen'}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Karte>
      </div>
    </>
  );
}
